import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { Separator } from '@nesso/ui/primitives/separator';

// The trailing ghost button a settings row action uses, exported so a row needing more than one
// trailing action (e.g. a location's Edit/Delete/Make Default) can render each with it.
export function SettingsRowActionButton({ className, ...props }: ComponentProps<typeof Button>) {
    return <Button type="button" variant="ghost" size="default" {...props} />;
}

type SettingsRowAction = {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    // Distinct accessible name when the visible label (e.g. "Add") repeats across rows.
    ariaLabel?: string;
};

// `action` is sugar for the trailing ghost button most rows use; `trailing` takes anything else
// (a toggle group, a chevron). Never both — they render into the same slot.
type SettingsRowSlot =
    | { action?: SettingsRowAction; trailing?: never }
    | { trailing?: ReactNode; action?: never };

type SettingsRowProps = {
    label: string;
    badge?: ReactNode;
    caption?: ReactNode;
    className?: string;
    children?: ReactNode;
} & SettingsRowSlot;

export function SettingsRow({
    label,
    badge,
    caption,
    className,
    children,
    ...props
}: SettingsRowProps) {
    const { action, trailing } = props;
    return (
        <>
            <div className={cn('flex flex-col gap-6 pb-2', className)}>
                <div className="flex flex-col">
                    {/* Centred, not top-aligned: the button, toggle group and switch differ in height.
                        A caption row holds the Edit button's height unless a trailing control sets its own. */}
                    <div
                        className={cn(
                            'flex items-center justify-between gap-1',
                            caption && !trailing && 'min-h-9',
                        )}
                    >
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                            <p className="text-base font-semibold">{label}</p>
                            {badge}
                        </div>
                        {action ? (
                            <SettingsRowActionButton
                                onClick={action.onClick}
                                disabled={action.disabled}
                                aria-label={action.ariaLabel}
                            >
                                {action.label}
                            </SettingsRowActionButton>
                        ) : (
                            trailing
                        )}
                    </div>
                    {/* a div, not a p — callers pass nodes such as a loading Skeleton */}
                    {caption && (
                        <div className="min-w-0 text-sm font-medium text-muted-foreground">
                            {caption}
                        </div>
                    )}
                </div>
                {children}
            </div>
            {/* Figma draws its divider at zero height, in the pixel row above its layout
                position, so the line's own pixel comes out of the margin above it, not below. */}
            <Separator className="mt-[15px] mb-4" />
        </>
    );
}
